/**
 * PASSWORD POLICY — one pure module, no Nest, no DOM, no network.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DUPLICATED ON PURPOSE, AND THE DUPLICATION IS TESTED.
 *
 *   backend/src/game/auth/password-policy.ts    ← the CONTROL
 *   frontend/src/engine/auth/password-policy.ts ← the UX mirror
 *
 * Byte-identical, pinned by `POLICY_FINGERPRINT`, which both suites assert. See
 * the same note in `common-passwords.ts`.
 *
 * The client copy exists so the meter can react to a keystroke without a round
 * trip. It is NOT the control: `GameAuthService.register` runs this same
 * function server-side on every request and is the only verdict that decides
 * whether an account is created. A client check is a courtesy to the honest
 * user; it stops nobody who edits the page.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHAT THIS DELIBERATELY DOES NOT DO — and why (NIST SP 800-63B §5.1.1.2):
 *
 *  • NO FORCED COMPOSITION. No "must contain an upper-case letter and a symbol".
 *    Composition rules measurably make passwords WORSE: they push users to
 *    `Password1!` — which this module rejects, because it is on the list —
 *    while adding almost nothing to the search space of a real attacker.
 *  • NO SHORT MAXIMUM. The cap is 128 characters, which is core's, so a
 *    passphrase or a password-manager string is never truncated by us.
 *  • NO FORCED EXPIRY, no "cannot reuse your last N" — neither is expressible
 *    here and both are counter-productive.
 *
 * WHAT IT DOES INSTEAD, in the order that actually buys security:
 *  1. LENGTH (12+). The one rule with an unambiguous positive effect.
 *  2. A BREACH-STYLE DENYLIST, checked against several normalisations of the
 *     input, so `P@ssw0rd2024!` is caught by the entry `password`. This is the
 *     rule that stops the guesses attackers actually make first.
 *  3. NOT DERIVED FROM THE ACCOUNT — no username, no email local part. Those
 *     are public; a password containing one is a password already half-known.
 *  4. NOT A KEYBOARD WALK OR A REPEAT.
 *
 * A NOTE ON bcrypt. Core hashes with bcryptjs, which considers only the first
 * 72 BYTES of the password. Two passwords sharing a 72-byte prefix are the same
 * password to it. We do not refuse anything over 72 bytes — that would punish
 * the users with the best passwords — but nothing here should be read as
 * claiming that character 100 of a 128-character password adds entropy.
 */
import { COMMON_PASSWORD_SET } from './common-passwords';

/** The floor. Above core's `@Length(8, 128)`; the single most effective rule. */
export const MIN_PASSWORD_LENGTH = 12;

/** Core's ceiling, kept as-is. Long is good; we only refuse what we cannot store. */
export const MAX_PASSWORD_LENGTH = 128;

/** How much of a username/email fragment counts as "derived from the account". */
export const MIN_IDENTITY_FRAGMENT = 4;

/** What is wrong with a password. Every value maps to a translation key. */
export type PasswordProblem =
  | 'blank'
  | 'too_short'
  | 'too_long'
  | 'common'
  | 'predictable'
  | 'contains_identity';

/** The meter's four rungs. Advisory only — `acceptable` is what gates. */
export type PasswordStrength = 'weak' | 'fair' | 'good' | 'strong';

/** Public account facts a password must not be built from. */
export interface PasswordIdentity {
  readonly email?: string | null;
  readonly username?: string | null;
}

export interface PasswordAssessment {
  /** True when NO problem was found. This, and only this, gates registration. */
  readonly acceptable: boolean;
  /** Every problem found, in a stable order, so the UI can list them all. */
  readonly problems: readonly PasswordProblem[];
  readonly strength: PasswordStrength;
  /** 0–3, aligned to `strength`. `weak` whenever `acceptable` is false. */
  readonly score: 0 | 1 | 2 | 3;
  /** Estimated bits, rounded down. Advisory; see `estimateBits`. */
  readonly bits: number;
  /** Length in UTF-16 code units, which is what the length rules count. */
  readonly length: number;
}

/** Translation key for a problem. Keys live in `frontend/src/engine/i18n/locales`. */
export function passwordProblemKey(problem: PasswordProblem): string {
  switch (problem) {
    case 'blank':
      return 'errors.validation.required';
    case 'too_short':
      return 'auth.password_rules.length';
    case 'too_long':
      return 'errors.validation.max_length';
    case 'common':
      return 'auth.password_rules.common';
    case 'predictable':
      return 'auth.password_rules.predictable';
    case 'contains_identity':
      return 'auth.password_rules.contains_identity';
  }
}

/** Interpolation parameters the message for `problem` needs, if any. */
export function passwordProblemParams(problem: PasswordProblem): { count: number } | undefined {
  if (problem === 'too_short') return { count: MIN_PASSWORD_LENGTH };
  if (problem === 'too_long') return { count: MAX_PASSWORD_LENGTH };
  return undefined;
}

/* ══════════════════════════════════════════════════════ the denylist check ══ */

/**
 * Leet substitutions, applied when normalising for the denylist lookup.
 *
 * The point is that `P@ssw0rd` and `password` are the same password to anyone
 * running a cracking rule set, and pretending otherwise is how `Passw0rd!`
 * became one of the most common passwords in existence.
 */
const LEET: ReadonlyMap<string, string> = new Map([
  ['@', 'a'], ['4', 'a'], ['8', 'b'], ['(', 'c'], ['3', 'e'], ['6', 'g'],
  ['1', 'i'], ['!', 'i'], ['|', 'i'], ['0', 'o'], ['5', 's'], ['$', 's'],
  ['7', 't'], ['+', 't'], ['2', 'z'],
]);

function deleet(value: string): string {
  let out = '';
  for (const ch of value) out += LEET.get(ch) ?? ch;
  return out;
}

/**
 * Every form of the password worth looking up in the denylist.
 *
 * A user who types `Sunshine2024!` has chosen `sunshine`; the year and the bang
 * are the decoration every wordlist rule already strips. Checking only the raw
 * string would let the entire list be bypassed by a suffix.
 */
export function denylistVariants(password: string): string[] {
  const lower = password.toLowerCase();
  const variants = new Set<string>();
  const add = (value: string): void => {
    if (value.length >= 4) variants.add(value);
  };

  // Both orders matter. Stripping FIRST catches `Sunshine2024!` → `sunshine`;
  // de-leeting first and stripping after catches `P@ssw0rd2024!`, where the
  // decoration and the substitutions are tangled together (`0` is part of the
  // word, `2024` is not, and only de-leeting can tell them apart).
  for (const base of strippings(lower)) {
    add(base);
    for (const unleeted of strippings(deleet(base))) add(unleeted);
  }
  return [...variants];
}

/** The forms of `value` left after removing the usual decoration. */
function strippings(value: string): string[] {
  const trimmedEnd = value.replace(/[^a-z0-9]+$/g, '');
  return [
    value,
    trimmedEnd,
    // A trailing year or counter: `sunshine2024` → `sunshine`.
    trimmedEnd.replace(/[0-9]+$/g, ''),
    trimmedEnd.replace(/^[^a-z0-9]+/g, ''),
    // The letters alone — `s.u.n.s.h.i.n.e` and `s1u2n3` collapse to the word.
    value.replace(/[^a-z]/g, ''),
  ];
}

/** Is this password (or a trivial decoration of one) on the bundled list? */
export function isCommonPassword(password: string): boolean {
  return denylistVariants(password).some((v) => COMMON_PASSWORD_SET.has(v));
}

/* ═══════════════════════════════════════════════════ walks and repetition ══ */

/** Rows and runs an attacker enumerates before trying anything else. */
const WALKS: readonly string[] = [
  '0123456789',
  'abcdefghijklmnopqrstuvwxyz',
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
  'qwertzuiop',
  'azertyuiop',
  '1qaz2wsx3edc4rfv5tgb6yhn7ujm',
];

/**
 * Each walk and its reverse, TRIPLED, so a slice that wraps past the end
 * (`098765432109`, `yzabcdefghij`) is still found — a wrap is not novelty.
 * Tripling rather than doubling because a slice may be as long as the walk
 * itself and still straddle the seam.
 */
const WALK_HAYSTACKS: readonly string[] = WALKS.flatMap((w) => {
  const reversed = [...w].reverse().join('');
  return [w.repeat(3), reversed.repeat(3)];
});

/**
 * A password that is nothing but a slice of a keyboard row, an alphabet or a
 * digit run — `abcdefghijkl`, `098765432109`, `qwertyuiopqwe`.
 *
 * Only the WHOLE password counts. A walk *inside* a longer password (`horse
 * qwerty battery`) is not the problem; a password that IS one is.
 */
export function isKeyboardWalk(password: string): boolean {
  const lower = password.toLowerCase();
  if (lower.length < 4) return false;
  return WALK_HAYSTACKS.some((h) => h.includes(lower));
}

/**
 * Too few distinct characters to be worth its length: `aaaaaaaaaaaa`,
 * `abababababab`, `123123123123`.
 *
 * Threshold: at least 5 distinct characters, and at least a quarter of the
 * length must be distinct. Both are needed — 5 distinct characters is fine in
 * a 12-character password and absurd in a 60-character one.
 */
export function isTooRepetitive(password: string): boolean {
  const distinct = new Set(password).size;
  if (password.length === 0) return false;
  return distinct < 5 || distinct * 4 < password.length;
}

/* ══════════════════════════════════════════════════════════ the identity ══ */

/** The public fragments of an account that a password must not contain. */
export function identityFragments(identity: PasswordIdentity | undefined): string[] {
  if (identity === undefined) return [];
  const out: string[] = [];
  const push = (value: string | null | undefined): void => {
    if (typeof value !== 'string') return;
    const v = value.trim().toLowerCase();
    if (v.length >= MIN_IDENTITY_FRAGMENT) out.push(v);
  };

  push(identity.username);
  const email = identity.email?.trim().toLowerCase() ?? '';
  if (email.length > 0) {
    const at = email.indexOf('@');
    push(at > 0 ? email.slice(0, at) : email);
    // The domain's own label — `riftgate` out of `player@riftgate.example`.
    if (at > 0) {
      for (const label of email.slice(at + 1).split('.')) push(label);
    }
  }
  return out;
}

/** Does the password contain a fragment of the account it belongs to? */
export function containsIdentity(password: string, identity?: PasswordIdentity): boolean {
  const lower = password.toLowerCase();
  return identityFragments(identity).some((f) => lower.includes(f));
}

/* ═══════════════════════════════════════════════════════════ the verdict ══ */

/**
 * A rough entropy estimate, in bits, for the METER ONLY.
 *
 * `log2(pool) × effectiveLength`, where the effective length is capped by how
 * many distinct characters were actually used — otherwise `aaaaaaaaaaaaaaaaaaaa`
 * scores as a 20-character password. This is an ordering heuristic to drive a
 * four-rung bar, not a security claim; nothing gates on it.
 */
export function estimateBits(password: string): number {
  if (password.length === 0) return 0;
  let pool = 0;
  if (/[a-z]/.test(password)) pool += 26;
  if (/[A-Z]/.test(password)) pool += 26;
  if (/[0-9]/.test(password)) pool += 10;
  if (/[^A-Za-z0-9]/.test(password)) pool += 33;
  if (pool === 0) pool = 26;

  const distinct = new Set(password).size;
  const effective = Math.min(password.length, distinct * 2 + 2);
  return Math.floor(effective * (Math.log(pool) / Math.log(2)));
}

const STRENGTH_BY_SCORE: readonly PasswordStrength[] = ['weak', 'fair', 'good', 'strong'];

function scoreFor(bits: number): 0 | 1 | 2 | 3 {
  if (bits >= 90) return 3;
  if (bits >= 68) return 2;
  if (bits >= 50) return 1;
  return 0;
}

/**
 * The single verdict. Both the client meter and the server gate call this and
 * nothing else, so what the user is told and what the server enforces cannot
 * drift apart.
 */
export function assessPassword(
  password: string,
  identity?: PasswordIdentity,
): PasswordAssessment {
  const problems: PasswordProblem[] = [];

  if (password.length === 0) {
    problems.push('blank');
  } else {
    if (password.length < MIN_PASSWORD_LENGTH) problems.push('too_short');
    if (password.length > MAX_PASSWORD_LENGTH) problems.push('too_long');
    if (isCommonPassword(password)) problems.push('common');
    if (isKeyboardWalk(password) || isTooRepetitive(password)) problems.push('predictable');
    if (containsIdentity(password, identity)) problems.push('contains_identity');
  }

  const acceptable = problems.length === 0;
  const bits = estimateBits(password);
  // A rejected password is never shown as anything but weak: a green bar over a
  // password the server will refuse is a worse lie than no bar at all.
  const score = acceptable ? scoreFor(bits) : 0;

  return {
    acceptable,
    problems,
    score,
    strength: STRENGTH_BY_SCORE[score],
    bits,
    length: password.length,
  };
}

/**
 * Fingerprint of the RULES (not the word list — `common-passwords.ts` pins its
 * own). Asserted by both suites so the two copies of this file cannot drift.
 */
export const POLICY_FINGERPRINT = [
  MIN_PASSWORD_LENGTH,
  MAX_PASSWORD_LENGTH,
  MIN_IDENTITY_FRAGMENT,
  WALKS.length,
  LEET.size,
].join('.');
